// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/hooks/usePcp.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pcpService } from '../services/pcpService';
import type { ListaFiltros } from '../services/listaFiltros';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type { CriarPcpOrdemRequest, AtualizarPcpOrdemRequest } from '../types';

interface UsePcpParams {
  pagina?: number;
  tamanhoPagina?: number;
  /** Busca, status, período e ordem — aplicados no servidor. */
  filtros?: ListaFiltros;
}

export const usePcp = ({ pagina = 1, tamanhoPagina = 20, filtros = {} }: UsePcpParams = {}) => {
  const queryClient = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: ['pcp', pagina, tamanhoPagina, filtros],
    queryFn: () => pcpService.getAll(pagina, tamanhoPagina, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada letra da busca
  });

  const ordens       = paginado?.itens       ?? [];
  const total        = paginado?.total        ?? 0;
  const totalPaginas = paginado?.totalPaginas ?? 1;
  const resumo       = paginado?.resumo;

  const createMutation = useMutation({
    mutationFn: (request: CriarPcpOrdemRequest) => pcpService.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pcp'] });
      toast.success('Ordem de Produção criada com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarPcpOrdemRequest }) =>
      pcpService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pcp'] });
      toast.success('Ordem de Produção atualizada com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => pcpService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pcp'] });
      toast.success('Ordem de Produção excluída com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return {
    ordens,
    total,
    totalPaginas,
    resumo,
    isLoading,
    isFetching,
    createOrdem: createMutation.mutate,
    updateOrdem: updateMutation.mutate,
    deleteOrdem: deleteMutation.mutate,
    isCreating:  createMutation.isPending,
  };
};
